import api from './index';
import { getUserId } from './auth';

async function postPricing(applicationId, data, userId) {
  const authUserId = userId || getUserId();

  if (!authUserId) {
    throw new Error('User not authenticated');
  }

  if (!applicationId) {
    throw new Error('Application ID is required');
  }

  // Send data flat - don't wrap in preferences key
  // Include id if present for updates
  const response = await api.post('/pricing', {
    id: data.id, // Pass through for updates
    applicationId,
    ...data,
    userId: authUserId
  });

  return response.data;
}

async function fetchPricingByApplication(applicationId) {
  if (!applicationId) {
    throw new Error('Application ID is required');
  }

  try {
    const response = await api.get('/pricing', {
      params: { applicationId }
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Pricing Service
 * Handles pricing preferences and calculations for trademark applications
 */
export const pricingService = {
  /**
   * Save pricing preferences to the database
   */
  savePricingPreferences: async (preferences, userId = null) => {
    try {
      const { applicationId, userId: ignoredUserId, ...data } = preferences;
      // Keep id in data for updates
      return await postPricing(applicationId, data, userId);
    } catch (error) {
      console.error('Error saving pricing preferences:', error);
      throw error;
    }
  },
  
  /**
   * Update pricing preferences for an application
   */
  update: async (applicationId, data, userId = null) => {
    try {
      return await postPricing(applicationId, data, userId);
    } catch (error) {
      console.error('Error updating pricing preferences:', error);
      throw error;
    }
  },
  
  /**
   * Get saved pricing preferences for a user
   */
  getPricingPreferences: async (userId = null) => {
    try {
      const authUserId = userId || getUserId();
      
      if (!authUserId) {
        throw new Error('User not authenticated');
      }
      
      const response = await api.get('/pricing', { params: { userId: authUserId } });
      return response.data;
    } catch (error) {
      console.error('Error getting pricing preferences:', error);
      throw error;
    }
  },
  
  /**
   * Get pricing preferences by direct pricingId (CANONICAL PATTERN)
   */
  getById: async (pricingId) => {
    try {
      if (!pricingId) {
        return null;
      }
      
      const response = await api.get(`/pricing/${pricingId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Error fetching pricing preferences by ID:', error);
      throw error;
    }
  },

  /**
   * Get saved pricing preferences by application ID (LEGACY METHOD - TO BE DEPRECATED)
   */
  getPricingPreferencesByApplicationId: async (applicationId) => {
    try {
      return await fetchPricingByApplication(applicationId);
    } catch (error) {
      console.error('Error getting pricing preferences by applicationId:', error);
      throw error;
    }
  },

  /**
   * Canonical helper for application lookup
   */
  getByApplicationId: async (applicationId) => {
    try {
      return await fetchPricingByApplication(applicationId);
    } catch (error) {
      console.error('Error fetching pricing by applicationId:', error);
      throw error;
    }
  }
};

export default pricingService;
